import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Color from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import Strike from '@tiptap/extension-strike';
import { 
  FaBold, 
  FaItalic, 
  FaUnderline, 
  FaStrikethrough, 
  FaListUl, 
  FaListOl, 
  FaAlignLeft, 
  FaAlignCenter, 
  FaAlignRight, 
  FaAlignJustify,
  FaLink,
  FaImage,
  FaPalette,
  FaHeading,
  FaQuoteLeft,
  FaCode,
  FaUndo,
  FaRedo,
  FaMagic,
  FaMinus,
  FaEraser,
  FaHighlighter,
  FaTable,
  FaSuperscript,
  FaSubscript,
  FaParagraph
} from 'react-icons/fa';

const TipTapEditor = ({ 
  value, 
  onChange, 
  placeholder = "✨ Describe the research opportunity, requirements, benefits, and what makes this position special...",
  className = ""
}) => {
  const editorRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable conflicting extensions that we're adding separately
        bold: true,
        italic: true,
        strike: false, // We'll use our custom Strike extension
        underline: false, // We'll use our custom Underline extension
        heading: true,
        bulletList: true,
        orderedList: true,
        blockquote: true,
        codeBlock: true,
        horizontalRule: true,
        hardBreak: true,
        paragraph: true,
        text: true,
      }),
      Placeholder.configure({
        placeholder,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 hover:text-blue-800 underline',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg',
        },
      }),
      Color,
      TextStyle,
      Underline,
      Strike,
    ],
    content: value,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html);
    },
    onFocus: ({ editor }) => {
    },
    onBlur: ({ editor }) => {
    },
    editorProps: {
      attributes: {
        class: 'h-full p-4 overflow-y-auto prose prose-sm max-w-none focus:outline-none cursor-text',
      },
    },
    // Enable immediate focus
    autofocus: false,
    editable: true,
    injectCSS: false,
  });

  // Update editor content when value prop changes
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [editor, value]);

  // Ensure editor is focused when component mounts
  useEffect(() => {
    if (editor && editorRef.current) {
      // Add click handler to ensure focus
      const handleEditorClick = () => {
        if (!editor.isFocused) {
          editor.commands.focus();
        }
      };

      const editorElement = editorRef.current;
      editorElement.addEventListener('click', handleEditorClick);
      
      return () => {
        editorElement.removeEventListener('click', handleEditorClick);
      };
    }
  }, [editor]);

  if (!editor) {
    return null;
  }

  // Improved button click handler - no setTimeout, immediate execution
  const handleButtonClick = (command, event) => {
    // Prevent default to avoid any interference
    event?.preventDefault();
    
    // Execute command immediately - TipTap handles focus internally
    command();
    
    // Ensure editor stays focused after command execution
    if (!editor.isFocused) {
      editor.commands.focus();
    }
  };

  // Enhanced handlers for specific actions
  const addLink = (e) => {
    e?.preventDefault();
    const url = window.prompt('Enter URL');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const addImage = (e) => {
    e?.preventDefault();
    const url = window.prompt('Enter image URL');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const setColor = (color, e) => {
    e?.preventDefault();
    editor.chain().focus().setColor(color).run();
  };

  const clearFormatting = (e) => {
    e?.preventDefault();
    editor.chain().focus().clearNodes().unsetAllMarks().run();
  };

  const addHorizontalRule = (e) => {
    e?.preventDefault();
    editor.chain().focus().setHorizontalRule().run();
  };

  const MenuBar = () => (
    <div className="bg-[#DB4F40] text-white p-4 rounded-t-xl border-b-2 border-[#FFD6D4]">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center">
          <FaMagic className="w-4 h-4 mr-2" />
          <span className="text-sm font-medium">Advanced Rich Text Editor</span>
        </div>
        <div className="text-xs opacity-80">
          Professional formatting made simple
        </div>
      </div>
      
      <div className="flex flex-wrap items-center gap-2">
        {/* Text Formatting */}
        <div className="flex items-center gap-1 bg-white/10 rounded-lg p-1">
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().toggleBold().run();
            }}
            className={`p-2 rounded hover:bg-white/20 transition-colors ${editor.isActive('bold') ? 'bg-white/20' : ''}`}
            title="Bold"
            type="button"
          >
            <FaBold className="w-3 h-3" />
          </button>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().toggleItalic().run();
            }}
            className={`p-2 rounded hover:bg-white/20 transition-colors ${editor.isActive('italic') ? 'bg-white/20' : ''}`}
            title="Italic"
            type="button"
          >
            <FaItalic className="w-3 h-3" />
          </button>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().toggleUnderline().run();
            }}
            className={`p-2 rounded hover:bg-white/20 transition-colors ${editor.isActive('underline') ? 'bg-white/20' : ''}`}
            title="Underline"
            type="button"
          >
            <FaUnderline className="w-3 h-3" />
          </button>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().toggleStrike().run();
            }}
            className={`p-2 rounded hover:bg-white/20 transition-colors ${editor.isActive('strike') ? 'bg-white/20' : ''}`}
            title="Strikethrough"
            type="button"
          >
            <FaStrikethrough className="w-3 h-3" />
          </button>
        </div>

        {/* Headings */}
        <div className="flex items-center gap-1 bg-white/10 rounded-lg p-1">
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().toggleHeading({ level: 1 }).run();
            }}
            className={`p-2 rounded hover:bg-white/20 transition-colors ${editor.isActive('heading', { level: 1 }) ? 'bg-white/20' : ''}`}
            title="Heading 1"
            type="button"
          >
            <FaHeading className="w-3 h-3" />
          </button>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().toggleHeading({ level: 2 }).run();
            }}
            className={`p-2 rounded hover:bg-white/20 transition-colors ${editor.isActive('heading', { level: 2 }) ? 'bg-white/20' : ''}`}
            title="Heading 2"
            type="button"
          >
            <span className="text-xs font-bold">H2</span>
          </button>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().toggleHeading({ level: 3 }).run();
            }}
            className={`p-2 rounded hover:bg-white/20 transition-colors ${editor.isActive('heading', { level: 3 }) ? 'bg-white/20' : ''}`}
            title="Heading 3"
            type="button"
          >
            <span className="text-xs font-bold">H3</span>
          </button>
        </div>

        {/* Lists */}
        <div className="flex items-center gap-1 bg-white/10 rounded-lg p-1">
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().toggleBulletList().run();
            }}
            className={`p-2 rounded hover:bg-white/20 transition-colors ${editor.isActive('bulletList') ? 'bg-white/20' : ''}`}
            title="Bullet List"
            type="button"
          >
            <FaListUl className="w-3 h-3" />
          </button>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().toggleOrderedList().run();
            }}
            className={`p-2 rounded hover:bg-white/20 transition-colors ${editor.isActive('orderedList') ? 'bg-white/20' : ''}`}
            title="Numbered List"
            type="button"
          >
            <FaListOl className="w-3 h-3" />
          </button>
        </div>

        {/* Alignment */}
        <div className="flex items-center gap-1 bg-white/10 rounded-lg p-1">
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().setTextAlign('left').run();
            }}
            className={`p-2 rounded hover:bg-white/20 transition-colors ${editor.isActive({ textAlign: 'left' }) ? 'bg-white/20' : ''}`}
            title="Align Left"
            type="button"
          >
            <FaAlignLeft className="w-3 h-3" />
          </button>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().setTextAlign('center').run();
            }}
            className={`p-2 rounded hover:bg-white/20 transition-colors ${editor.isActive({ textAlign: 'center' }) ? 'bg-white/20' : ''}`}
            title="Align Center"
            type="button"
          >
            <FaAlignCenter className="w-3 h-3" />
          </button>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().setTextAlign('right').run();
            }}
            className={`p-2 rounded hover:bg-white/20 transition-colors ${editor.isActive({ textAlign: 'right' }) ? 'bg-white/20' : ''}`}
            title="Align Right"
            type="button"
          >
            <FaAlignRight className="w-3 h-3" />
          </button>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().setTextAlign('justify').run();
            }}
            className={`p-2 rounded hover:bg-white/20 transition-colors ${editor.isActive({ textAlign: 'justify' }) ? 'bg-white/20' : ''}`}
            title="Justify"
            type="button"
          >
            <FaAlignJustify className="w-3 h-3" />
          </button>
        </div>

        {/* Links and Media */}
        <div className="flex items-center gap-1 bg-white/10 rounded-lg p-1">
          <button
            onMouseDown={addLink}
            className="p-2 rounded hover:bg-white/20 transition-colors"
            title="Add Link"
            type="button"
          >
            <FaLink className="w-3 h-3" />
          </button>
          <button
            onMouseDown={addImage}
            className="p-2 rounded hover:bg-white/20 transition-colors"
            title="Add Image"
            type="button"
          >
            <FaImage className="w-3 h-3" />
          </button>
        </div>

        {/* Colors */}
        <div className="flex items-center gap-1 bg-white/10 rounded-lg p-1">
          <button
            onMouseDown={(e) => setColor('#DB4F40', e)}
            className="p-2 rounded hover:bg-white/20 transition-colors"
            title="Primary Color"
            type="button"
          >
            <FaPalette className="w-3 h-3" />
          </button>
          <div className="flex gap-1">
            {['#DB4F40', '#1B222A', '#FFD6D4', '#4F46E5', '#059669', '#DC2626', '#EA580C', '#CA8A04'].map((color) => (
              <button
                key={color}
                onMouseDown={(e) => setColor(color, e)}
                className="w-4 h-4 rounded border border-white/30 hover:scale-110 transition-transform"
                style={{ backgroundColor: color }}
                title={`Color: ${color}`}
                type="button"
              />
            ))}
          </div>
        </div>

        {/* Special Elements */}
        <div className="flex items-center gap-1 bg-white/10 rounded-lg p-1">
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().toggleBlockquote().run();
            }}
            className={`p-2 rounded hover:bg-white/20 transition-colors ${editor.isActive('blockquote') ? 'bg-white/20' : ''}`}
            title="Quote"
            type="button"
          >
            <FaQuoteLeft className="w-3 h-3" />
          </button>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().toggleCodeBlock().run();
            }}
            className={`p-2 rounded hover:bg-white/20 transition-colors ${editor.isActive('codeBlock') ? 'bg-white/20' : ''}`}
            title="Code Block"
            type="button"
          >
            <FaCode className="w-3 h-3" />
          </button>
          <button
            onMouseDown={addHorizontalRule}
            className="p-2 rounded hover:bg-white/20 transition-colors"
            title="Horizontal Rule"
            type="button"
          >
            <FaMinus className="w-3 h-3" />
          </button>
        </div>

        {/* Text Tools */}
        <div className="flex items-center gap-1 bg-white/10 rounded-lg p-1">
          <button
            onMouseDown={clearFormatting}
            className="p-2 rounded hover:bg-white/20 transition-colors"
            title="Clear Formatting"
            type="button"
          >
            <FaEraser className="w-3 h-3" />
          </button>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().setParagraph().run();
            }}
            className={`p-2 rounded hover:bg-white/20 transition-colors ${editor.isActive('paragraph') ? 'bg-white/20' : ''}`}
            title="Paragraph"
            type="button"
          >
            <FaParagraph className="w-3 h-3" />
          </button>
        </div>

        {/* History */}
        <div className="flex items-center gap-1 bg-white/10 rounded-lg p-1">
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().undo().run();
            }}
            disabled={!editor.can().undo()}
            className="p-2 rounded hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Undo"
            type="button"
          >
            <FaUndo className="w-3 h-3" />
          </button>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().redo().run();
            }}
            disabled={!editor.can().redo()}
            className="p-2 rounded hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Redo"
            type="button"
          >
            <FaRedo className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`bg-white rounded-xl border-2 border-[#FFD6D4] overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300 ${className}`}>
      <MenuBar />
      <div className="h-[24rem] md:h-[28rem]" ref={editorRef}>
        <EditorContent 
          editor={editor} 
          className="h-full p-4 overflow-y-auto prose prose-sm max-w-none focus:outline-none cursor-text"
          style={{
            '--tw-prose-body': '#1B222A',
            '--tw-prose-headings': '#DB4F40',
            '--tw-prose-links': '#DB4F40',
            '--tw-prose-bold': '#1B222A',
            '--tw-prose-counters': '#6B7280',
            '--tw-prose-bullets': '#6B7280',
            '--tw-prose-hr': '#E5E7EB',
            '--tw-prose-quotes': '#6B7280',
            '--tw-prose-quote-borders': '#E5E7EB',
            '--tw-prose-captions': '#6B7280',
            '--tw-prose-code': '#1B222A',
            '--tw-prose-pre-code': '#E5E7EB',
            '--tw-prose-pre-bg': '#1F2937',
            '--tw-prose-th-borders': '#D1D5DB',
            '--tw-prose-td-borders': '#E5E7EB',
          }}
        />
      </div>
    </div>
  );
};

export default TipTapEditor;